import type { CreativeContextState } from '@/types/creative-library';
import { useCallback, useEffect, useRef, useState } from "react";
import type { CreativeContextSelection } from "@digital-twins/shared-types";
import { creativeLibraryApi } from "@/lib/api/creative-library";

export function useThreadCreativeContext(
  threadToken?: string | null,
  enabled = true,
) {
  const [state, setState] = useState<CreativeContextState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const observedToken = useRef(threadToken);
  observedToken.current = threadToken;
  const reload = useCallback(async () => {
    if (!threadToken || !enabled) return;
    setLoading(true);
    try {
      const next = await creativeLibraryApi.context(threadToken);
      if (observedToken.current === threadToken) {
        setState(next);
        setError(false);
      }
    } catch {
      if (observedToken.current === threadToken) setError(true);
    } finally {
      if (observedToken.current === threadToken) setLoading(false);
    }
  }, [threadToken, enabled]);
  useEffect(() => {
    setState(null);
    setSaving(false);
    setError(false);
    if (enabled) void reload();
  }, [reload, enabled]);
  const update = useCallback(
    async (selection: CreativeContextSelection) => {
      if (!threadToken || !state || saving) return;
      setSaving(true);
      setError(false);
      try {
        const next = await creativeLibraryApi.updateContext(
          threadToken,
          state.revision,
          selection,
        );
        if (observedToken.current === threadToken) setState(next);
      } catch {
        if (observedToken.current === threadToken) setError(true);
      } finally {
        if (observedToken.current === threadToken) setSaving(false);
      }
    },
    [threadToken, state, saving],
  );
  return { ...state, loading, saving, error, reload, update };
}
